'use client';

import { Button } from '@pickleballinc/react-ui';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import moment from 'moment';
import { useRouter } from 'next/navigation';
import { Fragment } from 'react';

import { useGetLeagueScheduleQueryData } from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/queries';
import ScheduleArticleSkeleton from '@/modules/league/details/schedule/ScheduleArticleSkeleton';
import { ScheduleData } from '@/modules/league/new-components/types';

export default function Schedule({ session, params }: ScheduleData) {
  const { slug, sessionId } = params;

  const { data: schedule, isRefetching } = useGetLeagueScheduleQueryData(sessionId);

  const router = useRouter();

  return (
    <>
      {schedule && !isRefetching ? (
        <div className='mb-2  w-full overflow-x-auto lg:w-2/3 lg:p-3'>
          {Object.keys(schedule.schedule).map((key, index) => (
            <Fragment key={`schedule-by-month-${index}-${key}`}>
              <div
                className={`vertical-header-cell  inline-flex h-10 w-full  flex-nowrap border-x bg-gray-900  font-semibold text-white lg:flex${
                  index === 0 ? 'rounded-t-lg ' : null
                } `}
              >
                <div className='  self-center p-3 text-sm font-bold tracking-wide '>{key}</div>
              </div>

              {schedule?.schedule[key]?.map((gameDay, j) => (
                <div
                  key={j}
                  className={`border-lg flex w-full cursor-pointer  flex-col gap-2 border-x p-2${
                    j === (schedule?.schedule[key]?.length ?? 0) - 1 ? ' border-b ' : ''
                  }`}
                >
                  <div className='flex items-center ' key={gameDay.MatchDateID}>
                    <div className='flex-1'>
                      <CustomLink
                        href={`${process.env.NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${gameDay.LeagueSessionID}/matchdate/${gameDay.MatchDateID}?tab=rsvp`}
                        key={gameDay.MatchDateID}
                      >
                        <article
                          className='rounded-lg border-b bg-white text-darkblue-500 shadow-md hover:bg-gray-100 lg:p-0'
                          onClick={() => {
                            router.push(
                              `${process.env.NEXT_PUBLIC_PL_URL}/leagues/${slug}/session/${session.LeagueSessionID}/matchDate/${gameDay.MatchDateID}?tab=rsvp`
                            );
                          }}
                        >
                          <div className='flex w-full justify-between rounded-t-lg bg-gray-100 p-1 lg:hidden '>
                            <h3 className='text-lg font-medium text-gray-600 sm:text-xl'>
                              {gameDay.Title ? gameDay.Title : ''}
                            </h3>
                          </div>
                          <div className='flex flex-col-reverse items-center p-2 sm:flex-row sm:gap-8'>
                            {!session.IsFlexPlaySchedule ? (
                              <div className=' hidden h-36 w-48 flex-col items-center justify-between rounded-l-lg bg-gray-200 p-6 lg:flex'>
                                <div className=' self-center text-sm'>
                                  {moment.utc(gameDay.FromDateTime).format(' D ')}
                                </div>
                                <div className='self-center text-xl font-semibold text-gray-600'>
                                  {moment.utc(gameDay.FromDateTime).format(' ddd ').toUpperCase()}
                                </div>
                                <div className=' self-center text-sm'>
                                  {moment.utc(gameDay.FromDateTime).format(' h:mm A ')}
                                </div>
                              </div>
                            ) : (
                              <div className=' hidden h-36 w-52 flex-col items-center justify-between rounded-l-lg bg-gray-200 p-6 lg:flex'>
                                <div className='self-center text-sm font-semibold text-gray-600'>
                                  {moment
                                    .utc(gameDay.FromDateTime)
                                    .format('D ddd h:mm A')
                                    .toUpperCase()}
                                </div>
                                <div className=' self-center text-sm font-semibold text-gray-600'>
                                  TO
                                </div>
                                <div className='self-center text-sm font-semibold text-gray-600'>
                                  {moment
                                    .utc(gameDay.ToDateTime)
                                    .format('D ddd h:mm A')
                                    .toUpperCase()}
                                </div>
                              </div>
                            )}

                            <div className='w-full'>
                              <div className='flex justify-between'>
                                <h3 className='text-lg font-medium text-gray-900 sm:text-xl'>
                                  {gameDay.LeagueTitle}
                                </h3>
                              </div>
                              <div className='mt-2 flex flex-col sm:gap-2'>
                                <p className='mt-1 text-sm text-gray-600'>{gameDay.ClubTitle}</p>
                                <span className='mt-2 text-xs text-gray-900 sm:mt-0'>
                                  Session {gameDay.SessionNumber} {gameDay.Title}
                                </span>
                                {gameDay.DateLockScores !== null ? (
                                  <Button
                                    variant='secondary'
                                    className='my-2 w-1/3 justify-center  !rounded-full !border-gray-200 !bg-gray-200 !p-1 !text-xs !font-bold !text-gray-900 lg:my-0 lg:w-1/6'
                                  >
                                    Completed
                                  </Button>
                                ) : (
                                  <Button
                                    variant='secondary'
                                    className=' my-2 w-2/3  justify-center !rounded-full !border-green-100 !bg-green-100 !p-1 !text-xs !font-bold !text-green-700 lg:my-0 lg:w-1/3'
                                  >
                                    Opt In{' '}
                                    {moment
                                      .utc(gameDay.OptInFromDateTime)
                                      .format(' MMM DD, YYYY h:mm A')}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </article>
                      </CustomLink>
                    </div>
                  </div>
                </div>
              ))}
            </Fragment>
          ))}
        </div>
      ) : (
        <ScheduleArticleSkeleton />
      )}
    </>
  );
}
